import { getLaunchSnapshot } from '@/demo/launch/state';
import { supabase } from "@/lib/supabase";
import { COACH_MOCK, DEMO_MODE } from "@/demo/config";
import { getDemo } from "@/demo/state";
import {
  createCoachClient,
  CoachError,
  CoachStatus,
  Brief,
  BillingSummary,
  AffiliateSummary,
  normalizeHandle,
} from "./coach-client";
export * from "./coach-client";
const MOCK_DAY = 86_400_000;
const mockAffiliate: AffiliateSummary = {
  enrolled: true,
  link: "https://coach.brandsmeetcreators.com?a=jordanmakes",
  code: "jordanmakes",
  commission_pct: 35,
  stats: { referrals: 4, active_referrals: 3, earnings_usd: 151.9, monthly_recurring_usd: 49.35, retention_pct: 75 },
  stats_as_of: new Date().toISOString(),
  recorded_earnings_usd: 151.9,
  referrals: [
    { name: "Maya R.", plan: "annual", status: "active", joined_at: new Date(Date.now() - 3 * MOCK_DAY).toISOString(), earned_usd: 86.45 },
    { name: "Chris T.", plan: "monthly", status: "active", joined_at: new Date(Date.now() - 20 * MOCK_DAY).toISOString(), earned_usd: 32.9 },
    { name: "Ana P.", plan: "monthly", status: "active", joined_at: new Date(Date.now() - 41 * MOCK_DAY).toISOString(), earned_usd: 32.55 },
    { name: "folay", tiktok_added: false, plan: "monthly", status: "active", joined_at: new Date(Date.now() - 1 * MOCK_DAY).toISOString(), earned_usd: 14.1 },
    { name: "Sam K.", plan: "monthly", status: "cancelled", joined_at: new Date(Date.now() - 70 * MOCK_DAY).toISOString(), earned_usd: 0 },
  ],
  commissions: [
    { date: new Date(Date.now() - 3 * MOCK_DAY).toISOString(), payment_usd: 247, commission_usd: 86.45, kind: "first_payment", reversed: false },
    { date: new Date(Date.now() - 10 * MOCK_DAY).toISOString(), payment_usd: 47, commission_usd: 16.45, kind: "renewal", reversed: false },
    { date: new Date(Date.now() - 20 * MOCK_DAY).toISOString(), payment_usd: 47, commission_usd: 16.45, kind: "first_payment", reversed: false },
    { date: new Date(Date.now() - 41 * MOCK_DAY).toISOString(), payment_usd: 47, commission_usd: 16.45, kind: "first_payment", reversed: false },
    { date: new Date(Date.now() - 70 * MOCK_DAY).toISOString(), payment_usd: 47, commission_usd: 16.45, kind: "first_payment", reversed: true },
  ],
  whop_url: "https://whop.com/",
};
const real = createCoachClient(async () => {
  if (import.meta.env.VITE_COACH_SELF_SERVICE_ENABLED !== "1")
    throw new CoachError("Coach self-service is not connected to this portal yet. Use your existing setup email or contact support for help.", 503);
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}, fetch, recoverSession);

/**
 * One refresh for any number of requests that hit a 401 together. When the
 * refresh token itself is rejected, the session was ended elsewhere (signing
 * out of the BMC app signs out every device), so sign out here too: AuthGate
 * then sends the creator to /auth instead of leaving a page that only says
 * "sign in again". Network failures keep the session; the 401 message shows.
 */
let recovering: Promise<string | null> | null = null;
function recoverSession(): Promise<string | null> {
  recovering ??= (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (data.session?.access_token) return data.session.access_token;
      const status = (error as { status?: number } | null)?.status;
      if (error && status && status >= 400 && status < 500) await supabase.auth.signOut({ scope: "local" });
      return null;
    } finally {
      setTimeout(() => { recovering = null; }, 0);
    }
  })();
  return recovering;
}
/**
 * Start the mock on a freshly purchased, un-set-up seat so the first-run
 * onboarding screen is reachable before the worker endpoints exist. Set
 * VITE_COACH_MOCK_SCENARIO=awaiting_handle alongside VITE_COACH_API_MOCK=1.
 */
const AWAITING_HANDLE =
  import.meta.env.VITE_COACH_MOCK_SCENARIO === "awaiting_handle";
const initial = (): CoachStatus => ({
  seat: {
    exists: true,
    status: "active",
    tiktok_username: AWAITING_HANDLE ? "" : "jordancreates",
    tiktok_display_name: AWAITING_HANDLE ? null : "Jordan Creates",
    tiktok_avatar_url: null,
    // Nothing builds until a handle is chosen; fix-handle is what queues it.
    onboarding_status: AWAITING_HANDLE ? "awaiting_handle" : "ready",
    corpus_videos: AWAITING_HANDLE ? 0 : 128,
    corpus_newest: AWAITING_HANDLE ? null : "2026-09-05T14:00:00Z",
    dna_built: !AWAITING_HANDLE,
    // The slug is minted with the seat, so the connector link exists from the
    // start — onboarding step 3 hands it over before the build has finished.
    connector_url: "https://example.invalid/bmc-coach-demo",
  },
  app_link: { connected: false, showcase_ok: false },
  briefs: [
    { id: "gems", type: "hidden_gems", enabled: true, day: "Monday", hour: 9 },
    {
      id: "trends",
      type: "trending_videos",
      enabled: false,
      day: "Thursday",
      hour: 9,
    },
  ],
  billing: { plan: "annual", manage_url: null },
  affiliate: { link: "https://coach.brandsmeetcreators.com?a=jordancreates", commission_pct: 35 },
});
const initialBilling = (): BillingSummary => ({
  plan: "annual",
  status: "active",
  membership_status: "active",
  cancel_at_period_end: false,
  current_period_start: "2026-09-05T14:00:00Z",
  current_period_end: "2027-09-05T14:00:00Z",
  canceled_at: null,
  renewal_price: "$247.00 / year",
  payment_method: { brand: "visa", last4: "4242" },
  manage_url: null,
  payment_methods_url: null,
  can_cancel: true,
  can_resume: false,
  payments: [
    { id: "pay_demo_1", date: "2026-09-05T14:00:00Z", description: "Subscription started", amount: 247, refunded_amount: 0, currency: "USD", status: "paid", card_brand: "visa", card_last4: "4242" },
  ],
});
let mock = initial();
let mockBilling = initialBilling();
let readyAt = 0;
const wait = () => new Promise((r) => setTimeout(r, 350));
export function setMockCoach(patch: Partial<CoachStatus>) {
  mock = { ...mock, ...patch };
}
export function resetMockCoach() {
  mock = initial();
  mockBilling = initialBilling();
  readyAt = 0;
}
export function mockScenario(value: string) {
  mock = initial();
  if (value === "pending") mock.seat.onboarding_status = "pending_enrichment";
  if (value === "processing") mock.seat.onboarding_status = "processing";
  if (value === "failed") mock.seat.onboarding_status = "failed";
  if (value === "cancelled") mock.seat.status = "cancelled";
  if (value === "ready") mock.app_link = { connected: true, showcase_ok: true };
  if (["pending", "processing", "failed"].includes(value)) {
    mock.seat.dna_built = false;
    mock.seat.corpus_videos = 0;
    mock.seat.corpus_newest = null;
  }
  readyAt = 0;
}
export const coachApi: typeof real = COACH_MOCK
  ? {
      async status(_opts?: { fresh?: boolean }) {
        await wait();
        if (DEMO_MODE && getDemo().role !== "pro")
          throw new CoachError("No coach seat", 404);
        if (readyAt && Date.now() > readyAt) {
          mock.seat.onboarding_status = "ready";
          mock.seat.dna_built = true;
          mock.seat.corpus_videos = 96;
          mock.seat.corpus_newest = "2026-09-05T14:00:00Z";
          readyAt = 0;
        }
        const result = structuredClone(mock);
        if (DEMO_MODE) {
          const launch = getLaunchSnapshot();
          if (launch.setupStarted) {
            result.seat.tiktok_username = launch.handle;
            result.seat.onboarding_status = !launch.selected || launch.build === 'queued' ? 'pending_enrichment' : launch.build === 'running' ? 'processing' : launch.build === 'failed' ? 'failed' : 'ready';
            result.seat.dna_built = launch.selected && launch.build === 'idle';
            result.seat.corpus_videos = result.seat.dna_built ? launch.videos : 0;
          }
          if (launch.healthChecked) result.app_link = { connected: launch.health !== 'never_connected', showcase_ok: launch.health === 'healthy' };
        }
        return result;
      },
      async lookupHandle(handle) {
        const normalized = normalizeHandle(handle);
        await wait();
        // One reserved name so the "we could not find that account" branch is
        // reachable in review without needing the real endpoint.
        if (normalized.toLowerCase() === "notfound")
          throw new CoachError(
            `We could not find a public TikTok account called @${normalized}. Check the spelling and try again.`,
            404,
          );
        return {
          handle: normalized,
          display_name: normalized === "jordancreates" ? "Jordan" : normalized,
          avatar_url: null,
          public_videos: normalized === "jordancreates" ? 128 : 42,
        };
      },
      async fixHandle(handle) {
        const new_handle = normalizeHandle(handle);
        await wait();
        mock.seat = {
          ...mock.seat,
          tiktok_username: new_handle,
          onboarding_status: "pending_enrichment",
          dna_built: false,
          corpus_videos: 0,
          corpus_newest: null,
        };
        readyAt = Date.now() + 6000;
        return { status: "reprocessing", eta_minutes: 2 };
      },
      async resend() {
        await wait();
        return { sent: true, to: "jordan@example.com" };
      },
      async briefs(brief) {
        await wait();
        const id = brief.id || `brief-${Date.now()}`;
        const item = {
          id,
          type: brief.type,
          enabled: brief.enabled,
          day: brief.day || "Monday",
          hour: brief.hour ?? 9,
        };
        mock.briefs = mock.briefs.some((b) => b.id === id)
          ? mock.briefs.map((b) => (b.id === id ? item : b))
          : [...mock.briefs, item];
        return { ok: true, briefs: structuredClone(mock.briefs) };
      },
      async billing() {
        await wait();
        throw new CoachError("Billing is simulated in the meeting preview.", 0);
      },
      async subscription() {
        await wait();
        return structuredClone(mockBilling);
      },
      async affiliate(_opts?: { fresh?: boolean }) {
        await wait();
        return structuredClone(mockAffiliate);
      },
      async cancelSubscription(_reason: string, _otherText?: string) {
        await wait();
        mockBilling = { ...mockBilling, cancel_at_period_end: true, canceled_at: new Date().toISOString(), can_cancel: false, can_resume: true };
        return structuredClone(mockBilling);
      },
      async resumeSubscription() {
        await wait();
        mockBilling = { ...mockBilling, cancel_at_period_end: false, canceled_at: null, can_cancel: true, can_resume: false };
        return structuredClone(mockBilling);
      },
    }
  : real;
